import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Briefcase, DollarSign, Car, TrendingUp, CheckCircle, Upload, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const SalesRep = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    why_join: ''
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File too large', {
          description: 'Please upload a resume smaller than 5MB',
        });
        return;
      }
      // Check file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type', {
          description: 'Please upload a PDF or Word document',
        });
        return;
      }
      setResume(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!resume) {
      toast.error('Resume Required', {
        description: 'Please upload your resume to continue',
      });
      return;
    }

    if (formData.experience.length < 10) {
      toast.error('Sales Experience Too Short', {
        description: 'Please provide at least 10 characters describing your sales experience',
      });
      return;
    }

    if (formData.why_join.length < 10) {
      toast.error('Response Too Short', {
        description: 'Please provide at least 10 characters explaining why you want to join',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const submitFormData = new FormData();
      submitFormData.append('full_name', formData.full_name);
      submitFormData.append('email', formData.email);
      submitFormData.append('phone', formData.phone);
      submitFormData.append('location', formData.location);
      submitFormData.append('experience', formData.experience);
      submitFormData.append('why_join', formData.why_join);
      submitFormData.append('resume', resume);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sales-rep-application`, {
        method: 'POST',
        body: submitFormData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        
        // Check for validation errors
        if (errorData.detail && typeof errorData.detail === 'string' && errorData.detail.includes('validation')) {
          toast.error('Validation Error', {
            description: 'Please ensure all fields are filled correctly. Experience and "Why Join" fields need at least 10 characters.',
            duration: 6000,
          });
        } else {
          throw new Error(errorData.detail || 'Failed to submit application');
        }
        return;
      }

      await response.json();

      toast.success('Application Submitted Successfully!', {
        description: 'Thank you for your interest! Our team will review your application and contact you within 3-5 business days.',
        duration: 6000,
      });

      // Reset form
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        location: '',
        experience: '',
        why_join: ''
      });
      setResume(null);
      // Clear file input
      document.getElementById('resume-upload').value = '';
    } catch (error) {
      console.error('Sales rep application error:', error);
      toast.error('Failed to Submit Application', {
        description: error.message || 'Please try again or contact us directly at qncconstructions@gmail.com',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header with Logo */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/" className="inline-flex items-center">
            <img src="/qnc-logo.png" alt="QNC Co. Limited" className="h-12 w-auto" />
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/">
            <Button variant="ghost" className="mb-6 text-white hover:text-white hover:bg-blue-700">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>

          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Join Our Sales Team
            </h1>
            <p className="text-xl md:text-2xl mb-6 text-blue-100">
              Earn Up to USD $500 Per Confirmed Generator Sale
            </p>
            <p className="text-lg text-blue-50 max-w-3xl mx-auto">
              Be part of Jamaica's leading construction and generator solutions company. 
              Enjoy competitive commission, car upkeep support, and unlimited earning potential.
            </p>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Why Join QNC as a Sales Representative?
        </h2>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <CardTitle className="text-xl">High Commission</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Earn approximately <strong className="text-blue-600">USD $500</strong> for every confirmed generator sale you close
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Car className="w-6 h-6 text-green-600" />
              </div>
              <CardTitle className="text-xl">Car Upkeep</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Receive <strong className="text-green-600">car maintenance support</strong> to help you with transportation and client visits
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <CardTitle className="text-xl">Unlimited Potential</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                <strong className="text-purple-600">No cap on earnings</strong> - the more generators you sell, the more you earn
              </p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-100 hover:border-blue-300 transition-all duration-300 hover:shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Briefcase className="w-6 h-6 text-orange-600" />
              </div>
              <CardTitle className="text-xl">Quality Products</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Represent <strong className="text-orange-600">premium generators</strong> with Cummins engines and Stamford alternators
              </p>
            </CardContent>
          </Card>
        </div>

        {/* What We're Looking For */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">What We're Looking For</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Sales Experience</h4>
                <p className="text-gray-600 text-sm">Previous experience in sales, customer service, or business development (preferred but not required)</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Self-Motivated</h4>
                <p className="text-gray-600 text-sm">Driven, goal-oriented individuals who can work independently</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Strong Communicator</h4>
                <p className="text-gray-600 text-sm">Excellent communication and relationship-building skills</p>
              </div>
            </div>
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Access to Transportation</h4>
                <p className="text-gray-600 text-sm">Ability to travel for client meetings (car upkeep support provided)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Application Form */}
        <div className="max-w-3xl mx-auto">
          <Card className="border-2 border-blue-100 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
              <CardTitle className="text-2xl">Apply Now</CardTitle>
              <CardDescription className="text-base">
                Complete the form below to join our sales team
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (876) XXX-XXXX"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="location">Location/Parish *</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="Kingston, Jamaica"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="experience">Sales Experience * (minimum 10 characters)</Label>
                  <Textarea
                    id="experience"
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    placeholder="Tell us about your sales or relevant work experience..."
                    rows={4}
                    required
                    minLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.experience.length}/10 characters minimum
                  </p>
                </div>

                <div>
                  <Label htmlFor="why_join">Why do you want to join QNC as a Sales Rep? * (minimum 10 characters)</Label>
                  <Textarea
                    id="why_join"
                    value={formData.why_join}
                    onChange={(e) => setFormData({ ...formData, why_join: e.target.value })}
                    placeholder="What motivates you? What makes you a great fit for this role?"
                    rows={4}
                    required
                    minLength={10}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.why_join.length}/10 characters minimum
                  </p>
                </div>

                <div>
                  <Label htmlFor="resume-upload">Upload Resume *</Label>
                  <div className="mt-2">
                    <div className="flex items-center justify-center w-full">
                      <label
                        htmlFor="resume-upload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload className="w-10 h-10 mb-3 text-gray-400" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PDF or Word (MAX. 5MB)</p>
                          {resume && (
                            <p className="mt-2 text-sm text-blue-600 font-medium">
                              Selected: {resume.name}
                            </p>
                          )}
                        </div>
                        <input
                          id="resume-upload"
                          type="file"
                          className="hidden"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange}
                          required
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      Submit Application
                      <Briefcase className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-500 text-center">
                  By submitting this application, you agree to our{' '}
                  <Link to="/terms-of-use" className="text-blue-600 hover:underline">
                    Terms of Use
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" className="text-blue-600 hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SalesRep;
